import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { app, vapidKey } from "@/lib/firebase/firebase";
import { registerPwaServiceWorker } from "@/lib/pwa/registerServiceWorker";
import { toast } from "react-toastify";
import notificationApi from "./notificationApi";
import { parseJson } from "@/utils/safeParse/parseJson";

export interface NotificationData {
  id?: string;
  title: string;
  body: string;
  notifiactionType: string;
  timestamp?: number;
  read?: boolean;
  type?: "booking" | "promotion" | "system";
  url?: string;
  image?: string;
}

type NotificationListener = (notifications: NotificationData[]) => void;

// IndexedDB database configuration
const DB_NAME = "traveleasy-notifications";
const DB_VERSION = 1;
const STORE_NAME = "background-notifications";
const FLAG_STORE = "notification-flags";

class NotificationService {
  private notifications: NotificationData[] = [];
  private listeners: NotificationListener[] = [];
  private fcmToken: string | null = null;

  constructor() {
    // Load saved notifications from localStorage
    this.loadNotifications();

    // Set up listeners for notifications when in browser environment
    if (typeof window !== "undefined") {
      this.setupFirebaseMessaging();

      // Process any background notifications that were received while the app was closed
      this.processBackgroundNotifications();

      // Listen for visibility change to process background notifications when tab becomes active
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      // Process any background notifications when the tab becomes active
      this.processBackgroundNotifications();
    }
  };

  // Open IndexedDB database
  private openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        console.error("Error opening IndexedDB:", event);
        reject("Could not open IndexedDB");
      };

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store for notifications if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }

        // Create object store for flags if it doesn't exist
        if (!db.objectStoreNames.contains(FLAG_STORE)) {
          db.createObjectStore(FLAG_STORE, { keyPath: "id" });
        }
      };
    });
  }

  // Check if there are new background notifications in IndexedDB
  private hasNewBackgroundNotifications(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const db = await this.openDatabase();
        const transaction = db.transaction([FLAG_STORE], "readonly");
        const store = transaction.objectStore(FLAG_STORE);
        const request = store.get("has_new_bg_notifications");

        request.onsuccess = () => {
          resolve(request.result?.value === true);
        };

        request.onerror = (error) => {
          console.error("Error checking for new notifications:", error);
          reject(false);
        };
      } catch (error) {
        console.error("Error accessing IndexedDB:", error);
        resolve(false);
      }
    });
  }

  // Get all background notifications from IndexedDB
  private getBackgroundNotifications(): Promise<NotificationData[]> {
    return new Promise(async (resolve, reject) => {
      try {
        const db = await this.openDatabase();
        const transaction = db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = (error) => {
          console.error("Error getting background notifications:", error);
          reject([]);
        };
      } catch (error) {
        console.error("Error accessing IndexedDB for notifications:", error);
        resolve([]);
      }
    });
  }

  // Clear background notifications from IndexedDB
  private clearBackgroundNotifications(): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        const db = await this.openDatabase();

        // Clear the notifications store
        const notificationsTx = db.transaction([STORE_NAME], "readwrite");
        const notificationsStore = notificationsTx.objectStore(STORE_NAME);
        const clearRequest = notificationsStore.clear();

        clearRequest.onsuccess = () => {
          // Reset the flag
          const flagTx = db.transaction([FLAG_STORE], "readwrite");
          const flagStore = flagTx.objectStore(FLAG_STORE);
          flagStore.put({ id: "has_new_bg_notifications", value: false });

          resolve(true);
        };

        clearRequest.onerror = (error) => {
          console.error("Error clearing background notifications:", error);
          reject(error);
        };
      } catch (error) {
        console.error(
          "Error accessing IndexedDB for clearing notifications:",
          error
        );
        resolve(false);
      }
    });
  }

  // Process any background notifications that were stored by the service worker
  private async processBackgroundNotifications() {
    try {
      const hasNew = await this.hasNewBackgroundNotifications();

      if (hasNew) {
        const bgNotifications = await this.getBackgroundNotifications();

        if (bgNotifications.length > 0) {
          // Add background notifications to our notification list
          bgNotifications.forEach((notification) => {
            this.addNotification(notification);
          });

          // Clear the background notifications
          await this.clearBackgroundNotifications();
        }
      }
    } catch (error) {
      console.error("Error processing background notifications:", error);
    }
  }

  // Initialize Firebase Cloud Messaging
  private async setupFirebaseMessaging() {
    try {
      // Check if notification permission is granted
      if (Notification.permission === "granted") {
        await this.initializeFirebaseMessaging();
      } else if (Notification.permission !== "denied") {
        // Request permission
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          await this.initializeFirebaseMessaging();
        }
      }
    } catch (error) {
      console.warn("Error setting up Firebase Cloud Messaging:", error);
    }
  }

  // Initialize FCM and set up token and message handlers
  private async initializeFirebaseMessaging() {
    try {
      if (!("serviceWorker" in navigator)) {
        console.warn("Service workers are not supported in this browser");
        return;
      }

      const registration = await registerPwaServiceWorker();
      if (!registration) {
        return;
      }

      const messaging = getMessaging(app);

      this.fcmToken = await getToken(messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (this.fcmToken) {
        localStorage.setItem("fcmToken", this.fcmToken);
        this.registerDeviceWithBackend(this.fcmToken);
      }

      onMessage(messaging, (payload) => {
        this.handleIncomingNotification(payload);
      });
    } catch (error) {
      // e.g. invalid API key (installations/request-failed): push is optional,
      // so warn instead of error to keep it out of the error overlay/monitoring
      console.warn("Error initializing Firebase Messaging:", error);
    }
  }

  // Register device with backend
  private async registerDeviceWithBackend(token: string) {
    try {
      // Get user data if available
      const userData = localStorage.getItem("user");
      const user = userData ? parseJson(userData) : null;
      const userId = user?.customerCode || undefined;

      // Default topics to subscribe to
      const topics = ["all-users"];
      if (userId) {
        topics.push(`user-${userId}`);
      }

      // Register with backend
      await notificationApi.registerDevice(token, userId, topics);
    } catch (error) {
      console.error("Error registering device with backend:", error);
    }
  }

  // Handle incoming notification from Firebase
  private handleIncomingNotification(payload: any) {
    const notificationData: NotificationData = {
      id:
        payload.data?.notificationId ||
        `notification-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      title:
        payload.notification?.title ||
        payload.data?.title ||
        "New Notification",
      body: payload.notification?.body || payload.data?.body || "",
      notifiactionType:
        payload.notification?.notifiactionType ||
        payload.data?.notifiactionType ||
        "",
      timestamp: Date.now(),
      read: false,
      type: this.determineNotificationType(payload),
      url: payload.data?.deepLink || payload.data?.url,
      image: payload.notification?.image || payload.data?.imageUrl,
    };

    this.addNotification(notificationData);

    // Show browser notification if the app is not focused
    if (document.visibilityState !== "visible") {
      this.showBrowserNotification(notificationData);
    }
  }

  // Determine notification type based on payload content
  private determineNotificationType(
    payload: any
  ): "booking" | "promotion" | "system" {
    if (payload.data?.type) {
      const type = payload.data.type.toLowerCase();
      if (type === "booking") return "booking";
      if (type === "promotion") return "promotion";
      return "system";
    }

    const title = (
      payload.notification?.title ||
      payload.data?.title ||
      ""
    ).toLowerCase();
    const body = (
      payload.notification?.body ||
      payload.data?.body ||
      ""
    ).toLowerCase();

    if (
      title.includes("booking") ||
      title.includes("đặt chỗ") ||
      body.includes("booking") ||
      body.includes("đặt chỗ")
    ) {
      return "booking";
    }

    if (
      title.includes("promotion") ||
      title.includes("khuyến mãi") ||
      body.includes("promotion") ||
      body.includes("khuyến mãi")
    ) {
      return "promotion";
    }

    return "system";
  }

  // Display browser notification
  private showBrowserNotification(notification: NotificationData) {
    if (!("Notification" in window)) return;

    if (Notification.permission === "granted") {
      new Notification(notification.title, {
        body: notification.body,
        icon: notification.image || "/te-logo-small.png",
      });
    }
  }

  // Get current FCM token
  public async getFcmToken(): Promise<string | null> {
    // If we already have a token, return it
    if (this.fcmToken) {
      return this.fcmToken;
    }

    // Try to get it from localStorage
    const storedToken = localStorage.getItem("fcmToken");
    if (storedToken) {
      this.fcmToken = storedToken;
      return storedToken;
    }

    // If no token, try to request new one if permission is granted
    if (Notification.permission === "granted") {
      // try {
      //   const messaging = getMessaging(app);
      //   this.fcmToken = await getToken(messaging, {
      //     vapidKey: vapidKey,
      //     serviceWorkerRegistration:
      //       await navigator.serviceWorker.getRegistration(
      //         "/firebase-messaging-sw.js"
      //       ),
      //   });

      //   if (this.fcmToken) {
      //     localStorage.setItem("fcmToken", this.fcmToken);

      //     // Register device with backend
      //     this.registerDeviceWithBackend(this.fcmToken);
      //   }

      //   return this.fcmToken;
      // } catch (error) {
      //   console.error("Error getting FCM token:", error);
      //   return null;
      // }
    }

    return null;
  }

  // Request permission and get FCM token
  public async requestPermission(): Promise<string | null> {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        return await this.getFcmToken();
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return null;
    }
  }

  // Send notification to another user
  public async sendNotificationToUser(
    email: string,
    title: string,
    titleVi: string,
    message: string,
    messageVi: string,
    type: "Booking" | "Promotion" | "System" = "System",
    imageUrl?: string
  ) {
    try {
      await notificationApi.sendNotification({
        recipients: {
          appChannel: "WEB",
          userEmail: email,
        },
        notificationType: type,
        message: {
          title: {
            en: title,
            vi: titleVi || title,
          },
          text: {
            en: message,
            vi: messageVi || message,
          },
        },
        imageAttach: imageUrl,
      });

      return true;
    } catch (error) {
      console.error("Error sending notification:", error);
      return false;
    }
  }

  // Send broadcast notification to all users
  public async sendBroadcastNotification(
    title: string,
    titleVi: string,
    message: string,
    messageVi: string,
    type: "Booking" | "Promotion" | "System" = "System",
    imageUrl?: string
  ) {
    try {
      await notificationApi.sendNotification({
        recipients: {
          appChannel: "WEB",
          // No userEmail = send to all users
        },
        notificationType: type,
        message: {
          title: {
            en: title,
            vi: titleVi || title,
          },
          text: {
            en: message,
            vi: messageVi || message,
          },
        },
        imageAttach: imageUrl,
      });

      return true;
    } catch (error) {
      console.error("Error sending broadcast notification:", error);
      return false;
    }
  }

  // Add a new notification to the list
  public addNotification(notification: NotificationData) {
    if (!notification.id) {
      notification.id = `notification-${Date.now()}-${Math.floor(
        Math.random() * 1000
      )}`;
    }

    if (notification.timestamp === undefined) {
      notification.timestamp = Date.now();
    }

    // Check if notification already exists to avoid duplicates
    const existingIndex = this.notifications.findIndex(
      (n) => n.id === notification.id
    );
    if (existingIndex === -1) {
      this.notifications.unshift(notification);
      this.saveNotifications();
      this.notifyListeners();
    }
  }

  // Get all stored notifications
  public getNotifications(): NotificationData[] {
    return [...this.notifications];
  }

  // Mark a notification as read
  public async markAsRead(id: string) {
    const notification = this.notifications.find((n) => n.id === id);
    if (notification && !notification.read) {
      notification.read = true;
      this.saveNotifications();
      this.notifyListeners();

      // Also mark as read on the backend if the ID appears to be a backend ID
      if (id.startsWith("NOTI-")) {
        try {
          await notificationApi.markAsRead([id]);
        } catch (error) {
          console.error(
            "Error marking notification as read on backend:",
            error
          );
        }
      }
    }
  }

  // Mark all notifications as read
  public async markAllAsRead() {
    let changed = false;
    const backendIds: string[] = [];

    this.notifications.forEach((notification) => {
      if (!notification.read) {
        notification.read = true;
        changed = true;

        // Collect backend IDs
        if (notification.id && notification.id.startsWith("NOTI-")) {
          backendIds.push(notification.id);
        }
      }
    });

    if (changed) {
      this.saveNotifications();
      this.notifyListeners();

      // Also mark as read on the backend if we have any backend IDs
      if (backendIds.length > 0) {
        try {
          await notificationApi.markAsRead(backendIds);
        } catch (error) {
          console.error(
            "Error marking notifications as read on backend:",
            error
          );
        }
      }
    }
  }

  // Delete a notification
  public deleteNotification(id: string) {
    const initialLength = this.notifications.length;
    this.notifications = this.notifications.filter((n) => n.id !== id);

    if (this.notifications.length !== initialLength) {
      this.saveNotifications();
      this.notifyListeners();
    }
  }

  // Clear all notifications
  public clearNotifications() {
    if (this.notifications.length > 0) {
      this.notifications = [];
      this.saveNotifications();
      this.notifyListeners();
    }
  }

  // Get the count of unread notifications
  public getUnreadCount(): number {
    return this.notifications.filter((n) => !n.read).length;
  }

  // Add a listener for notification changes
  public addListener(listener: NotificationListener) {
    this.listeners.push(listener);
  }

  // Remove a listener
  public removeListener(listener: NotificationListener) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  // Notify all listeners of changes
  private notifyListeners() {
    this.listeners.forEach((listener) => {
      listener([...this.notifications]);
    });
  }

  // Load notifications from localStorage
  private loadNotifications() {
    try {
      const savedNotifications = typeof localStorage !== 'undefined' ?  localStorage.getItem("notifications") : undefined;
      if (savedNotifications) {
        this.notifications = parseJson(savedNotifications);
      }
    } catch (error) {
      console.error("Error loading notifications from localStorage:", error);
    }
  }

  // Save notifications to localStorage
  private saveNotifications() {
    try {
      localStorage.setItem("notifications", JSON.stringify(this.notifications));
    } catch (error) {
      console.error("Error saving notifications to localStorage:", error);
    }
  }
}

const notificationService = new NotificationService();
export default notificationService;
